/**
 * Remembered intake sessions (IndexedDB, hand-rolled — see `db.ts`).
 *
 * `{ sessionId, folderName, handleKey?, fileCount, createdAt, lastStep }`.
 * A return visit offers "Continue session <folder name>" for the latest
 * session that has a folder handle (DI-D13 c3).
 *
 * DI-2-3 adds the Turbine step's state, PER POSITION CLUSTER: the cluster's
 * centroid (the near query's point), the pilot's picks (turbine, instruction,
 * workflow), and — once created — the acquisition's ids (`dataAcquisitionId`,
 * `instructionId`, `assetId`, `siteId`), `intakeState` and stored `workflow`.
 * A session that reached the Turbine step is saved whatever the drop path
 * (picker or not): the Turbine step needs no file handle, only the clusters.
 * The browser remembers which session is active (DI-3-D: IndexedDB `meta`,
 * no longer the tab's `sessionStorage`), so a reload AND a new tab resume it.
 *
 * DI-2-4 adds, per cluster, the metadata round trip's answer (`roundTrip`:
 * created, duplicates, replayed, the cross-acquisition links) and the payload
 * hashes that were sent — so a reload still shows what the server said, and a
 * later step can tell which images the acquisition holds without the files.
 *
 * DI-3-C replaces the per-cluster Turbine / Sync steps by ONE decision for the
 * drop (`intent`, `uploadChoice`) and one proposal row per data set: each
 * cluster keeps the row's `decision`, the create `plan` frozen at Start, and
 * the runner's `phase` / `phaseTimes` / `error` / `metadataComplete`, written
 * on every transition, so a reload shows where each set stands and a resumed
 * session does not repeat a completed step. `clusterId` is the set's stable,
 * content-derived id (`runner/sets.ts`); sessions written before DI-3-C carry
 * DI-2-2's positional `c1`, `c2` … and their picks, which stay readable.
 *
 * Every call degrades to "nothing remembered" when IndexedDB is unavailable
 * (private windows, jsdom): remembering is a convenience, never a gate.
 */
import type { TurbineProposal } from "../verify/blockers.js";
import {
  SESSIONS,
  deleteMeta,
  getMeta,
  openIntakeDb,
  setMeta,
  tx,
} from "./db.js";
import type { RoundTripResult } from "../sync/roundTrip.js";
import type { HashResult } from "../parse/types.js";
import type { PhaseTimes, SetError, SetPhase } from "../runner/phases.js";
import type { AutoRetry } from "../runner/autoRetry.js";
import type { SetUpload, SetWithhold } from "../upload/state.js";
import { rememberSetTenant } from "../api/setTenants.js";
import type { ImageUploadMode } from "../../workflows/api/workflows.js";

/**
 * DI-2-9 adds `flights` — the step a resumed session reopens at. DI-3-C adds
 * `decide` (the Drop & decide page); `scan` / `turbine` / `sync` remain for
 * the sessions the M2 wizard saved.
 */
export type IntakeStep =
  | "drop"
  | "scan"
  | "decide"
  | "turbine"
  | "sync"
  | "flights";

/**
 * DI-3-C → M3C-04 — "These images are for". A `workflow` intent names the
 * VERSION picked (an own workflow or a shared injection node, workflows/v1)
 * and, for a shared node, the OWNER's tenant slug every call of its sets
 * sends (null = the caller's own tenant). `standalone` / `partner` are the
 * DI-3-C records a session written before M3C-04 carries: read, mapped by
 * `decide/intent.ts:validIntent`, never written again.
 */
export type IntentRecord =
  | { kind: "project"; projectId: string }
  | {
      kind: "workflow";
      workflowVersionId: string;
      targetTenantSlug: string | null;
      /** The owner's name (shared node only) — the owner chip's words. */
      targetTenantName?: string | null;
    }
  | { kind: "per_set" }
  | { kind: "standalone" }
  | { kind: "partner"; workflow: string };

/**
 * DI-3-C (N2) — upload the originals now, or after verification.
 * M5-NODE-01 — `"none"`: "don't upload", offered (and preselected) only under
 * a version whose `imageUpload` is `optional`. It is a DROP-level choice: the
 * runner maps it to a set's `uploadChoice: null` (today's partner path —
 * withhold at `verdict_ready`), so a SET's choice is never `"none"`.
 */
export type UploadChoice = "now" | "after" | "none";

/** M5-NODE-01 — the version's served image upload mode (`workflows/v1` 1.7.0). */
export type { ImageUploadMode };

/** "decide later" — the turbine (a placeholder, N1) or the instruction (field-created). */
export const DECIDE_LATER = "later" as const;

/** DI-3-C — the pilot's overrides on one proposal row (absent = the default). */
export interface SetDecision {
  /** An asset id, `"later"`, or null / absent = not picked. */
  turbine?: string | null;
  /** An instruction id, `"later"`, or null / absent = the proposal. */
  instruction?: string | null;
  /**
   * The workflow picked in the row (only where the row shows the select):
   * M3C-04 — an own VERSION id (workflows/v1); a pre-M3C-04 record holds a
   * preset key, which no longer matches a choice and so reads as "not
   * picked" (the intent's default applies).
   */
  workflow?: string | null;
  /**
   * DI-3-E — written at Start when the row offered SEVERAL turbines within
   * 333 m and one was taken: the verify card asks "Confirm the turbine:
   * 20611?" with the others as "It was …" (board 3, set 2).
   */
  turbineProposed?: TurbineProposal | null;
}

/** DI-3-C — exactly what one create sends, frozen at Start (a Retry resends it). */
export interface CreatePlan {
  instructionId: string | null;
  assetId: string | null;
  siteId: string | null;
  /** inspection-3/v1 2.3.0 — the standalone create under the pilot's own project. */
  projectId: string | null;
  /** inspection-3/v1 2.3.0 — the placeholder park + turbine (turbine deferred). */
  assetRef: {
    siteName: string;
    assetName: string;
    position: { lat: number; lon: number };
  } | null;
  /**
   * The legacy preset key, sent only by a pre-M3C-04 plan (a Retry resends
   * it as frozen); M3C-04 sends `workflowVersionId` instead.
   */
  workflow: string | null;
  /** The workflow the set runs under (the project's or the pick) — display + upload. */
  effectiveWorkflow: string;
  notes: string | null;
  /**
   * M3C-04 — the VERSION the create sends (`workflowVersionId`, inspection-3
   * 2.4.x): the row's pick or the shared node; null when a project defines
   * the workflow (the server copies the project's). Absent on a plan frozen
   * before M3C-04.
   */
  workflowVersionId?: string | null;
  /**
   * M3C-04 — the OWNER's tenant slug the create and every later call of the
   * set send as `X-Tenant-ID` (a shared injection node, M3C-02); null = own.
   */
  targetTenantSlug?: string | null;
  /** M3C-04 — the owner's name (the owner chip's words); null = own. */
  targetTenantName?: string | null;
  /**
   * ZV-MIS-01C (inspection-3/v1 2.8.0) — the set's centroid, sent with a
   * create that fills a mission (an instruction opened to the workflow); the
   * server re-checks the radius against it. Absent / null = not a mission.
   */
  dropPosition?: { lat: number; lon: number } | null;
}

/** DI-3-C — `metadata-complete`'s answer (intake/v1 1.9.0). */
export interface MetadataCompleteRecord {
  outcome: string;
  intakeState: string | null;
  jobId: string | null;
  at: string;
}

/** One position cluster's Turbine-step state (DI-2-3). */
export interface ClusterIntake {
  clusterId: string;
  centroid: { lat: number; lon: number };
  radiusM: number;
  imageCount: number;
  flightCount: number;
  /** First capture time in the cluster, ms (the camera clock as written). */
  firstCapturedAtMs: number | null;
  /** The pilot's picks — kept so a reload shows the same choices. */
  pickedTurbineId?: string | null;
  /** An instruction id, `"field-created"`, or null/undefined for "not picked". */
  pickedInstructionId?: string | null;
  pickedWorkflow?: string | null;
  /** Set once the acquisition exists. */
  externalId?: string;
  dataAcquisitionId?: string;
  instructionId?: string | null;
  assetId?: string | null;
  siteId?: string | null;
  intakeState?: string | null;
  workflow?: string | null;
  /** DI-2-4 — the last round trip's folded answer (incl. the payload-hash set sent). */
  roundTrip?: RoundTripResult;
  /**
   * M3C-04 — the tenant the set LIVES in, when it is not the caller's own:
   * the OWNER of the shared injection node it was created under (DI-D58).
   * Every call of the set sends it as `X-Tenant-ID` (the two intake fetch
   * helpers read it through `api/setTenants.ts`). `null` = own. Sessions
   * written before M3C-04 are migrated to `null` by the v3 upgrade (`db.ts`).
   */
  targetTenantSlug?: string | null;
  /** M3C-04 — the owner's name (the owner chip's words); null = own. */
  targetTenantName?: string | null;
  /** DI-3-C — the row's overrides, the frozen create, the runner's state. */
  decision?: SetDecision;
  plan?: CreatePlan;
  phase?: SetPhase;
  phaseTimes?: PhaseTimes;
  error?: SetError | null;
  /** DI-3-S68 B2 — the automatic retry's ladder: a reload neither restarts nor duplicates it. */
  autoRetry?: AutoRetry | null;
  uploadChoice?: UploadChoice | null;
  /**
   * M5-NODE-01 — the set's version's image upload mode at Start (the served
   * `WorkflowNode.imageUpload`). Read by Approve: under `optional` a set
   * that uploads ("now" / "after verification") sends `uploadImages: true`
   * (intake/v1 1.37.0). Absent on a record written before M5.
   */
  imageUpload?: ImageUploadMode;
  /**
   * M5-NODE-01 — the escape hatch's reason ("Upload images…", 1–2000
   * characters): every upload session of this set's upload sends it
   * (inspection-3/v1 2.6.0 `OpenUploadSessionRequest.reason`), so a resume
   * opens its session like the first pass. Absent on every other upload.
   */
  uploadReason?: string;
  metadataComplete?: MetadataCompleteRecord;
  /** DI-3-D — the board's words for the set, as the row showed them at Start. */
  turbineLabel?: string;
  instructionLabel?: string;
  /** DI-3-D — the members' bytes (the upload's ETA once DI-3-F sends them). */
  sizeBytes?: number;
  /** DI-3-D — how far the round trip got (written per batch): "paused at 57 %". */
  syncProgress?: { sent: number; total: number };
  /**
   * DI-3-D — the members' hashes, written when the set's round trip starts
   * (every member is hashed by then), keyed `name|size` — the file's base
   * name, DI-3-S15-FE (`rowHashKey`; a pre-S15 `relativePath|size` key is
   * read through its base name, `knownHashesOf`). A resume re-indexes the folder and hands these to the
   * index worker (DI-3-P2): a row whose key matches is head-parsed only and
   * reports these hashes without its bytes being read again; any other row
   * is hashed as on a first drop.
   */
  rowHashes?: Record<string, HashResult>;
  /**
   * DI-3-F — the originals' upload of an uploading set: its phase, the
   * ledger of committed blobs (`upload/ledger.ts`: file name → size, MD5,
   * committed at), the pass and its session / job, the counts the board
   * reads, and — "after verification" — when Approve answered
   * `approved_pending_upload`. Written on every phase change and every
   * committed blob; a new tab resumes from it (N8). No DB version bump: the
   * `sessions` store holds the record as a value, only its shape grows.
   */
  upload?: SetUpload;
  /** DI-3-F — a partner set's withhold (once, at `verdict_ready` / `needs_info`). */
  withhold?: SetWithhold;
  /**
   * DI-3-G — the add's ONE cluster: bound to this existing acquisition (no
   * create), the new files' keys its upload keeps, and the parent set's
   * number on the board ("data set 1").
   */
  boundTo?: string;
  uploadKeys?: string[];
  ordinal?: number;
}

/**
 * DI-3-D — the key a row's persisted hashes are filed under. DI-3-P2: defined
 * in `parse/hashKey.ts` (the index worker matches with the same formula).
 */
export { rowHashKey } from "../parse/hashKey.js";

export interface SessionRecord {
  sessionId: string;
  folderName: string;
  handleKey?: string;
  fileCount: number;
  createdAt: string;
  lastStep: IntakeStep;
  /** DI-2-3 — the clusters that go through the Turbine step. */
  clusters?: ClusterIntake[];
  /** DI-2-3 — the cluster the Turbine step is on. */
  activeClusterId?: string;
  /** DI-3-C — the drop's intent and upload choice, and when Start was pressed. */
  intent?: IntentRecord;
  uploadChoice?: UploadChoice;
  startedAt?: string;
  /**
   * DI-3-G — an "Add images…" session is a CHILD session: its own folder, its
   * own index, its own ledger (so the phases, the upload and the resume work
   * unchanged), bound to the parent's acquisition. The parent set it adds to
   * (null ids when the set was not started in this browser — a 409 held set,
   * an Intake history row).
   */
  parent?: {
    dataAcquisitionId: string;
    sessionId: string | null;
    clusterId: string | null;
  };
  /**
   * DI-3-P6 — when the session ENDED by itself: every started set left the
   * intake and nothing was pending in the browser (`runner/sessionEnd.ts`).
   * The record is KEPT (Intake history's folder names, `?open=`'s folder
   * handle for the previews); an ended record is never the active session,
   * never claimed, never re-indexed (`resume.ts:isLiveSession`) and never
   * offered as "Continue session". Absent = not ended ("Start over" drops a
   * session without writing it — it stays as it was).
   */
  endedAt?: string;
  /**
   * DI-3-S16-FE — where the dropped folder sits under the field-data root
   * (`files/handleStore.ts:ROOT_KEY`): the names from the root down to it,
   * the folder included (`["2026-09-01", "Park Nord", "WTG-05"]`). Written
   * at Start from `root.resolve(dropHandle)` when a root is granted and the
   * drop has a directory handle (only when it answers), or the first time
   * the folder was found under the root by name (a root remembered after the
   * drop). A resume walks it — no search. Absent = not known.
   */
  rootPath?: string[];
  /**
   * DI-3-S16-FE — the drop was loose FILES (or several folders): the browser
   * named no folder, the record keeps the placeholder name "Upload"
   * (`LOOSE_DROP_NAME`). Such a record is never looked for under the root;
   * the picker asks (or a folder picked on the card, `handle:set:<daId>`).
   */
  looseDrop?: boolean;
  /**
   * M5-NODE-01 — an escape-hatch record ("Upload images…"): ONE set, the
   * existing acquisition's, phase `running`, upload "now" with the reason.
   * Written by `escapeHatch/escapeRecord.ts`, run by the upload lane only
   * (never the active session, never "Continue session"), ended by the lane
   * once its upload is present.
   */
  escapeHatch?: { dataAcquisitionId: string };
}

/** DI-3-S16-FE — the record's name when the browser named no folder. */
export const LOOSE_DROP_NAME = "Upload";

/**
 * DI-3-S16-FE — was the drop loose files? The flag, else (a record written
 * before it) the placeholder name with no folder handle.
 */
export function isLooseDrop(
  rec: Pick<SessionRecord, "looseDrop" | "folderName" | "handleKey">,
): boolean {
  if (rec.looseDrop !== undefined) return rec.looseDrop;
  return !rec.handleKey && rec.folderName === LOOSE_DROP_NAME;
}

/**
 * One stored record rewritten in ONE readwrite transaction (get + put), so a
 * concurrent write of the record (the lane's ledger) is never overwritten by
 * a stale copy. Resolves the record written, or null (none / any failure —
 * a convenience, swallowed).
 */
async function patchStoredRecord(
  sessionId: string,
  patch: (cur: SessionRecord) => SessionRecord,
): Promise<SessionRecord | null> {
  let db: IDBDatabase;
  try {
    db = await openIntakeDb();
  } catch {
    return null;
  }
  try {
    return await new Promise<SessionRecord | null>((resolve) => {
      const store = db.transaction(SESSIONS, "readwrite").objectStore(SESSIONS);
      const get = store.get(sessionId) as IDBRequest<SessionRecord | undefined>;
      get.onsuccess = () => {
        const cur = get.result;
        if (!cur) {
          resolve(null);
          return;
        }
        const next = patch(cur);
        learnSetTenants(next);
        const put = store.put(next);
        put.onsuccess = () => {
          resolve(next);
        };
        put.onerror = () => {
          resolve(null);
        };
      };
      get.onerror = () => {
        resolve(null);
      };
    });
  } finally {
    db.close();
  }
}

/**
 * DI-3-S16-FE — file `rootPath` on a stored record (one transaction). A
 * convenience: a failure is swallowed.
 */
export async function setRootPath(
  sessionId: string,
  rootPath: readonly string[],
): Promise<void> {
  await patchStoredRecord(sessionId, (cur) => ({
    ...cur,
    rootPath: [...rootPath],
  }));
}

const renamedListeners = new Set<(sessionId: string, name: string) => void>();

/**
 * DI-3-S18-FE — the record's folder was found again under ANOTHER name (a
 * folder renamed on disk, found by its files under the root; or picked for
 * the paused session): THAT record — by `sessionId`, no other — takes the
 * folder's current name and, when known, where it sits under the root, so
 * the next lookup is the free one (the recorded path). `rootPath` null: the
 * new place is not known — a path recorded for the OLD name is dropped (it
 * names a folder that is gone), one for the same name is kept. One
 * transaction; listeners (`onSessionFolderRenamed`) hear the id once it is
 * written. Resolves whether a record was rewritten.
 */
export async function renameSessionFolder(
  sessionId: string,
  name: string,
  rootPath: readonly string[] | null,
): Promise<boolean> {
  if (!name) return false;
  const written = await patchStoredRecord(sessionId, (cur) => {
    const next: SessionRecord = { ...cur, folderName: name };
    if (rootPath && rootPath.length > 0) next.rootPath = [...rootPath];
    else if (cur.folderName !== name) delete next.rootPath;
    return next;
  });
  if (!written) return false;
  for (const l of renamedListeners) l(sessionId, name);
  return true;
}

/** DI-3-S18-FE — hear `renameSessionFolder`'s rewrites (My Intake re-reads). */
export function onSessionFolderRenamed(
  fn: (sessionId: string, name: string) => void,
): () => void {
  renamedListeners.add(fn);
  return () => {
    renamedListeners.delete(fn);
  };
}

/**
 * M3C-04 — the fetch helpers learn which tenant a set lives in from the
 * moment its record is read or written here (a resume, the board, a new tab):
 * every cluster with an acquisition and a target tenant is remembered.
 */
function learnSetTenants(rec: SessionRecord | null | undefined): void {
  for (const c of rec?.clusters ?? []) {
    const slug = c.targetTenantSlug ?? c.plan?.targetTenantSlug ?? null;
    if (c.dataAcquisitionId && slug)
      rememberSetTenant(c.dataAcquisitionId, {
        slug,
        name: c.targetTenantName ?? c.plan?.targetTenantName ?? null,
      });
  }
}

export async function saveSession(rec: SessionRecord): Promise<void> {
  learnSetTenants(rec);
  await tx(SESSIONS, "readwrite", (s) => s.put(rec)).catch(() => undefined);
}

export async function getSession(
  sessionId: string,
): Promise<SessionRecord | null> {
  const rec = await tx<SessionRecord | undefined>(
    SESSIONS,
    "readonly",
    (s) => s.get(sessionId) as IDBRequest<SessionRecord | undefined>,
  ).catch(() => undefined);
  learnSetTenants(rec);
  return rec ?? null;
}

/** Read-modify-write of one session; returns the stored record (or null). */
export async function updateSession(
  sessionId: string,
  patch: (rec: SessionRecord) => SessionRecord,
): Promise<SessionRecord | null> {
  const cur = await getSession(sessionId);
  if (!cur) return null;
  const next = patch(cur);
  await saveSession(next);
  return next;
}

/** Merge `patch` into one cluster of one session. */
export function updateCluster(
  sessionId: string,
  clusterId: string,
  patch: Partial<ClusterIntake>,
): Promise<SessionRecord | null> {
  return updateSession(sessionId, (rec) => ({
    ...rec,
    clusters: (rec.clusters ?? []).map((c) =>
      c.clusterId === clusterId ? { ...c, ...patch } : c,
    ),
  }));
}

export async function listSessions(): Promise<SessionRecord[]> {
  const all = await tx<SessionRecord[]>(
    SESSIONS,
    "readonly",
    (s) => s.getAll() as IDBRequest<SessionRecord[]>,
  ).catch(() => [] as SessionRecord[]);
  for (const r of all) learnSetTenants(r);
  return all.sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  );
}

/**
 * M3C-04 — the OWNER slugs of the shared injection nodes this browser's
 * sets were created under (the lists' per-owner queries, AC-5), with the
 * owner's name. The caller's own slug is never one of them.
 */
export async function sharedOwnersOfSessions(): Promise<
  { slug: string; name: string | null }[]
> {
  return sharedOwnersOf(await listSessions());
}

/** `sharedOwnersOfSessions` over records already read (a page's `sessions`). */
export function sharedOwnersOf(
  records: readonly SessionRecord[],
): { slug: string; name: string | null }[] {
  const out = new Map<string, string | null>();
  for (const r of records)
    for (const c of r.clusters ?? []) {
      const slug = c.targetTenantSlug ?? c.plan?.targetTenantSlug ?? null;
      if (slug && !out.has(slug))
        out.set(slug, c.targetTenantName ?? c.plan?.targetTenantName ?? null);
    }
  return [...out].map(([slug, name]) => ({ slug, name }));
}

/**
 * The newest session that can be continued (it has a remembered folder
 * handle). DI-3-P6 — an ENDED session is not continued: its sets left the
 * intake.
 */
export async function latestResumableSession(): Promise<SessionRecord | null> {
  return (
    (await listSessions()).find(
      (s) =>
        s.handleKey !== undefined &&
        s.endedAt === undefined &&
        s.escapeHatch === undefined,
    ) ?? null
  );
}

export async function deleteSession(sessionId: string): Promise<void> {
  await tx(SESSIONS, "readwrite", (s) => s.delete(sessionId)).catch(
    () => undefined,
  );
}

export function newSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID();
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ── The browser's active session (DI-2-3 → DI-3-D) ──────────────────────

/**
 * DI-3-D (N8) — the active session lives in IndexedDB's `meta` store, so
 * every tab of this browser sees the same one: a new tab (or the tab the
 * pilot opens the next morning) resumes it. Until DI-3-D it lived in the
 * TAB's `sessionStorage` (`aeriosiq-intake:active-session`), which a new tab
 * does not inherit — that key is no longer read or written. All three calls
 * are async now; like every call here they degrade to "nothing remembered".
 */
export const ACTIVE_SESSION_KEY = "activeSession";

export async function rememberActiveSession(sessionId: string): Promise<void> {
  await setMeta(ACTIVE_SESSION_KEY, sessionId);
}

export async function activeSessionId(): Promise<string | null> {
  const v = await getMeta<string>(ACTIVE_SESSION_KEY);
  return typeof v === "string" && v.length > 0 ? v : null;
}

export async function forgetActiveSession(): Promise<void> {
  await deleteMeta(ACTIVE_SESSION_KEY);
}
